<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, ref } from 'vue'
import { blobUrl } from '@/lib/http.js'
import { formatDate, formatMoney } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import UiBadge from '@/components/ui/UiBadge.vue'
import UiButton from '@/components/ui/UiButton.vue'
import RevisionCompare from '@/components/offers/RevisionCompare.vue'

/**
 * A contract's versions (D-175), newest first: what each is to the contract (made, its scope
 * extended, changed), how it came about (the customer accepted our offer, or we confirmed the
 * customer's order — D-174), when and by whom, with what document, on which offer's revision —
 * and what it changed against the same offer's version before it. Then the offers it stands on.
 */
const props = defineProps({ record: { type: Object, required: true } })
const versions = computed(() => [...(props.record.versions || [])].sort((a, b) => b.version_no - a.version_no))
const comparing = ref(null)

async function openFile(a) {
  try {
    window.open(await blobUrl(a.url), '_blank', 'noopener')
  } catch (e) {
    reportError(e)
  }
}
</script>

<template>
  <section class="card p-4" data-testid="contract-versions" data-privacy-cells>
    <h3 class="mb-2 flex items-center gap-2 font-semibold"><i class="fa-solid fa-code-branch text-faint" aria-hidden="true"></i> {{ $t('contract.versions') }}</h3>
    <ol class="divide-y divide-[var(--border)]">
      <li v-for="v in versions" :key="v.uuid" class="py-2 text-sm" :data-testid="`contract-version-${v.version_no}`">
        <div class="flex flex-wrap items-center gap-1.5">
          <span class="font-semibold">v{{ v.version_no }}</span>
          <UiBadge :tone="v.kind === 'order' ? 'green' : 'blue'">{{ $t(`offers.life.kinds.${v.kind}`) }}</UiBadge>
          <span v-if="v.version_no === record.current_version_no" class="text-xs text-muted">{{ $t('contract.latest') }}</span>
          <span class="flex-1"></span>
          <span class="tabular-nums">{{ v.total != null ? formatMoney(v.total, v.offer?.currency_code || record.currency_code) : '' }}</span>
        </div>
        <p v-if="v.withdrawn_at" class="my-1 rounded bg-red-50 px-2 py-1 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200" :data-testid="`contract-version-withdrawn-${v.version_no}`"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i> {{ $t('contract.withdrawn', { date: formatDate(v.withdrawn_at), offer: v.offer?.label || '', n: v.revision_label }) }}</p>
        <p class="text-xs text-muted">
          <template v-if="v.form === 'order_confirmed'">{{ $t('offers.life.orderLine', { ordered: formatDate(v.ordered_on), confirmed: formatDate(v.accepted_on) }) }}</template>
          <template v-else>{{ $t('offers.life.acceptLine', { date: formatDate(v.accepted_on) }) }}</template>
          <span v-if="v.accepted_by"> · {{ v.accepted_by }}</span>
          <span v-if="v.note"> · {{ v.note }}</span>
        </p>
        <p class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <router-link v-if="v.offer" class="link" :to="{ name: 'offer-editor', params: { uuid: v.offer.uuid } }">{{ v.offer.label }} · {{ v.revision_label }}</router-link>
          <span v-else class="text-faint">{{ $t('contract.offerHidden') }}</span>
          <button v-if="v.attachment" type="button" class="link" @click="openFile(v.attachment)"><i class="fa-solid fa-paperclip" aria-hidden="true"></i> {{ v.attachment.name }}</button>
          <RecordLink v-if="v.opportunity && v.opportunity.uuid !== record.opportunity?.uuid" class="link" entity="opportunity" :uuid="v.opportunity.uuid">{{ v.opportunity.label }}</RecordLink>
          <UiButton v-if="v.offer && v.replaces_revision_no" size="xs" variant="ghost" icon="fa-code-compare" :data-testid="`contract-compare-${v.version_no}`" @click="comparing = { offer: v.offer, from: v.replaces_revision_no, to: v.revision_no, fromLabel: v.replaces_revision_label, toLabel: v.revision_label }">{{ $t('contract.compare', { a: v.replaces_revision_no, b: v.revision_no }) }}</UiButton>
        </p>
      </li>
      <li v-if="!versions.length" class="py-2 text-sm text-faint">{{ $t('contract.noVersions') }}</li>
    </ol>
    <template v-if="(record.offers || []).length > 1">
      <h4 class="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-faint">{{ $t('contract.offers') }}</h4>
      <ul class="space-y-1 text-sm">
        <li v-for="o in record.offers" :key="o.uuid"><router-link class="link" :to="{ name: 'offer-editor', params: { uuid: o.uuid } }">{{ o.label }}</router-link></li>
      </ul>
    </template>
    <RevisionCompare v-if="comparing" :offer-uuid="comparing.offer.uuid" :from="comparing.from" :to="comparing.to" :from-label="comparing.fromLabel" :to-label="comparing.toLabel" :currency="comparing.offer.currency_code || record.currency_code" @close="comparing = null" />
  </section>
</template>
